import { useEffect, useMemo, useState } from 'react';
import type { ErpConnectionView, ErpDocumentMode, ErpDocumentSettings, ErpImportMode } from '../../api';
import { accountsOf, erpApi } from '../../api';
import { ConfirmDialog, ErrorNotice } from '../ui';
import { Callout, Checkbox, Field, Input, Select } from '../primitives';
import { documentModeLabel, formatDay, readinessMissing, readinessLabel, treasuryAccountPattern } from './labels';
import { useAction } from './useAction';

interface AccountRow {
  /** Stable while the form is open, so a row keeps its input focus when another is removed. */
  id: number;
  method: string;
  account: string;
}

const FALLBACK = '*';

function rowsOf(accounts: Record<string, string>): AccountRow[] {
  return Object.entries(accounts)
    .filter(([method]) => method !== FALLBACK)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([method, account], index) => ({ id: index, method, account }));
}

function dayOf(iso: string | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

/** The settings as the form can change them, in one order, for "is there anything to save". */
function fingerprint(settings: ErpDocumentSettings, testCompany: boolean): string {
  return JSON.stringify([
    testCompany,
    settings.mode,
    dayOf(settings.since),
    settings.company_name,
    settings.shipping_article,
    settings.default_unit,
    settings.due_days,
    settings.import_mode,
    settings.receipts_automatic,
    Object.entries(accountsOf(settings)).sort(([a], [b]) => a.localeCompare(b)),
  ]);
}

/**
 * What the connection does with the store's invoices and their payments
 * (CO-147). Off by default; Preview builds the documents and keeps them to be
 * read and sends none; Live sends them to the ERP automatically. There is no
 * mode that builds files for a person to upload by hand (CO-179). Live is
 * offered only for a company the operator of this Commerce Ops has allowed and
 * once the steps in "Before sending" are done, and turning it on is confirmed.
 */
export function ErpDocumentSettingsForm({
  connection,
  canManage,
  onChanged,
  counts,
}: {
  connection: ErpConnectionView;
  canManage: boolean;
  onChanged: () => void;
  /** The invoices built and waiting, and those that need a fix, for the confirmation. */
  counts?: { ready: number; blocked: number };
}) {
  const stored = connection.settings.documents;
  const erpName = connection.provider_info.display_name;
  const action = useAction();

  const [mode, setMode] = useState<ErpDocumentMode>(stored.mode);
  const [since, setSince] = useState(dayOf(stored.since));
  const [companyName, setCompanyName] = useState(stored.company_name);
  const [shippingArticle, setShippingArticle] = useState(stored.shipping_article);
  const [defaultUnit, setDefaultUnit] = useState(stored.default_unit);
  const [dueDays, setDueDays] = useState(String(stored.due_days));
  const [importMode, setImportMode] = useState<ErpImportMode>(stored.import_mode);
  const [receiptsAutomatic, setReceiptsAutomatic] = useState(stored.receipts_automatic);
  const [fallback, setFallback] = useState(accountsOf(stored)[FALLBACK] ?? '');
  const [rows, setRows] = useState<AccountRow[]>(() => rowsOf(accountsOf(stored)));
  const [nextRow, setNextRow] = useState(1000);
  const [testCompany, setTestCompany] = useState(Boolean(connection.settings.test_company));
  const [activating, setActivating] = useState(false);

  // A reload of the connection resets the form to what is stored.
  useEffect(() => {
    setMode(stored.mode);
    setSince(dayOf(stored.since));
    setCompanyName(stored.company_name);
    setShippingArticle(stored.shipping_article);
    setDefaultUnit(stored.default_unit);
    setDueDays(String(stored.due_days));
    setImportMode(stored.import_mode);
    setReceiptsAutomatic(stored.receipts_automatic);
    setFallback(accountsOf(stored)[FALLBACK] ?? '');
    setRows(rowsOf(accountsOf(stored)));
  }, [stored]);
  useEffect(() => setTestCompany(Boolean(connection.settings.test_company)), [connection.settings.test_company]);

  const dueValid = /^\d{1,3}$/.test(dueDays) && Number(dueDays) <= 365;
  const accountValid = (value: string) => value.trim() === '' || treasuryAccountPattern.test(value.trim());
  const fallbackValid = accountValid(fallback);
  const rowProblem = (row: AccountRow, index: number): string | null => {
    const method = row.method.trim().toLowerCase();
    if (method === '' && row.account.trim() === '') return null;
    if (method === '') return 'Enter the payment method’s code.';
    if (method === FALLBACK) return 'Use the “any other payment method” field for this.';
    if (method.length > 64) return 'A payment method code is at most 64 characters.';
    if (rows.findIndex((other) => other.method.trim().toLowerCase() === method) !== index) return 'This payment method is listed twice.';
    if (row.account.trim() === '') return 'Enter the account its money goes to.';
    if (!accountValid(row.account)) return 'An account looks like 5311 (cash) or 5121 (bank).';
    return null;
  };
  const rowProblems = rows.map(rowProblem);
  const valid = dueValid && fallbackValid && rowProblems.every((problem) => problem === null);

  const draft = useMemo((): ErpDocumentSettings => {
    const accounts: Record<string, string> = {};
    for (const row of rows) {
      const method = row.method.trim().toLowerCase();
      if (method && row.account.trim()) accounts[method] = row.account.trim();
    }
    if (fallback.trim()) accounts[FALLBACK] = fallback.trim();
    return {
      mode,
      since: since ? `${since}T00:00:00Z` : undefined,
      company_name: companyName.trim(),
      shipping_article: shippingArticle.trim(),
      default_unit: defaultUnit.trim().toUpperCase(),
      due_days: Number(dueDays),
      import_mode: importMode,
      payment_accounts: accounts,
      receipts_automatic: receiptsAutomatic,
    };
  }, [mode, since, companyName, shippingArticle, defaultUnit, dueDays, importMode, receiptsAutomatic, fallback, rows]);

  const storedTest = Boolean(connection.settings.test_company);
  const dirty = useMemo(() => fingerprint(draft, testCompany) !== fingerprint(stored, storedTest), [draft, stored, testCompany, storedTest]);

  const save = () =>
    action.run(
      'save',
      async () => {
        await erpApi.updateConnection(connection.id, {
          documents: draft,
          ...(testCompany !== storedTest ? { test_company: testCompany } : {}),
        });
        onChanged();
      },
      'Invoice settings saved.',
    );

  const liveAllowed = connection.documents_live_allowed;
  // Preview builds exactly what live would send, so it needs the same settings.
  const on = mode !== 'off';
  // Turning sending on is deliberate: it is confirmed with the facts in front of the person.
  const turningOn = mode === 'live' && stored.mode !== 'live';
  const missing = readinessMissing(connection.documents_readiness).filter((id) => id !== 'allowed');
  const liveOffered = liveAllowed && missing.length === 0;

  return (
    <div className="erp-doc-settings" data-testid="erp-document-settings">
      {!connection.store_data_enabled ? (
        <Callout tone="warning" title="Invoices cannot be built yet" testId="erp-documents-no-store-data">
          This Commerce Ops does not take the invoices and orders of your stores, so there is nothing to build invoices from.
          Ask whoever runs it to turn on store data.
        </Callout>
      ) : null}
      {stored.mode === 'live' && !liveAllowed ? (
        <Callout tone="warning" title={`Sending to ${erpName} is not allowed any more`} testId="erp-documents-live-withdrawn">
          Sending is switched on here, but the operator of this Commerce Ops no longer allows it for this company. Nothing is
          built or sent until they allow it again.
        </Callout>
      ) : null}
      <div className="erp-settings-grid">
        <Field
          label="What to do with invoices"
          hint={liveAllowed ? undefined : `Sending to ${erpName} needs the operator of this Commerce Ops to allow this company first.`}
        >
          <Select
            id="erp-doc-mode"
            value={mode}
            onChange={(event) => setMode(event.target.value as ErpDocumentMode)}
            disabled={!canManage}
          >
            <option value="off">{documentModeLabel.off}</option>
            <option value="preview">{documentModeLabel.preview}</option>
            <option value="live" disabled={!liveOffered && stored.mode !== 'live'}>
              {documentModeLabel.live}
              {liveAllowed ? (missing.length > 0 ? ' (finish “Before sending” first)' : '') : ' (not allowed for this company)'}
            </option>
          </Select>
        </Field>
        <Field
          label="Take invoices issued from"
          hint="Earlier invoices are never taken. Leave empty to start today when you turn this on."
        >
          <Input
            id="erp-doc-since"
            type="date"
            value={since}
            onChange={(event) => setSince(event.target.value)}
            disabled={!canManage}
          />
        </Field>
        <Checkbox
          id="erp-doc-test-company"
          checked={testCompany}
          onChange={(event) => setTestCompany(event.target.checked)}
          disabled={!canManage}
          label="This is a test company"
        />
        {on ? (
          <>
            <Field label="Company name on invoices and receipts">
              <Input
                id="erp-doc-company"
                autoComplete="off"
                maxLength={120}
                value={companyName}
                placeholder={connection.name}
                onChange={(event) => setCompanyName(event.target.value)}
                disabled={!canManage}
              />
            </Field>
            <Field
              label="Shipping article"
              hint={<>The service article in {erpName} that shipping is invoiced under. Needed when an invoice charges shipping.</>}
            >
              <Input
                id="erp-doc-shipping"
                autoComplete="off"
                maxLength={64}
                spellCheck={false}
                value={shippingArticle}
                onChange={(event) => setShippingArticle(event.target.value)}
                disabled={!canManage}
              />
            </Field>
            <Field
              label="Unit of measure"
              hint={<>Used when {erpName} does not say how an article is counted.</>}
            >
              <Input
                id="erp-doc-unit"
                autoComplete="off"
                maxLength={8}
                spellCheck={false}
                value={defaultUnit}
                onChange={(event) => setDefaultUnit(event.target.value)}
                disabled={!canManage}
              />
            </Field>
            <Field
              label="Days until an invoice falls due"
              error={dueValid ? null : 'Enter a whole number from 0 to 365.'}
            >
              <Input
                id="erp-doc-due"
                type="number"
                inputMode="numeric"
                min={0}
                max={365}
                value={dueDays}
                onChange={(event) => setDueDays(event.target.value)}
                disabled={!canManage}
              />
            </Field>
            {on ? (
              <>
                <Field label="After sending">
                  <Select
                    id="erp-doc-import"
                    value={importMode}
                    onChange={(event) => setImportMode(event.target.value as ErpImportMode)}
                    disabled={!canManage}
                  >
                    <option value="staged">{erpName} keeps it for a person to finish</option>
                    <option value="automatic">{erpName} finishes the import itself</option>
                  </Select>
                </Field>
                <Checkbox
                  id="erp-doc-receipts"
                  checked={receiptsAutomatic}
                  onChange={(event) => setReceiptsAutomatic(event.target.checked)}
                  disabled={!canManage}
                  label="Send receipts on their own"
                  hint={
                    <>
                      {erpName} links a receipt only to an invoice it has already validated. Leave this off and send them
                      yourself once the invoices are validated.
                    </>
                  }
                />
              </>
            ) : null}
            <fieldset className="erp-doc-accounts erp-settings-grid__wide">
              <legend>Where each payment method’s money goes</legend>
              <p className="field__help" id="erp-doc-accounts-help">
                The {erpName} account a receipt is booked to: 5311 for cash, 5121 for the bank. A receipt is built only for
                a payment method listed here or covered by “any other”.
              </p>
              <div className="erp-doc-accounts__row">
                <Field
                  label="Any other payment method"
                  error={fallbackValid ? null : 'An account looks like 5311 (cash) or 5121 (bank).'}
                >
                  <Input
                    id="erp-doc-account-any"
                    autoComplete="off"
                    spellCheck={false}
                    value={fallback}
                    placeholder="5121"
                    onChange={(event) => setFallback(event.target.value)}
                    disabled={!canManage}
                    aria-describedby="erp-doc-accounts-help"
                  />
                </Field>
              </div>
              {rows.map((row, index) => {
                const problem = rowProblems[index];
                const error = problem ? `erp-doc-row-${row.id}-error` : undefined;
                return (
                  <div className="erp-doc-accounts__row" key={row.id} data-testid="erp-doc-account-row">
                    <Field label="Payment method code">
                      <Input
                        id={`erp-doc-row-${row.id}-method`}
                        autoComplete="off"
                        maxLength={64}
                        spellCheck={false}
                        value={row.method}
                        placeholder="cashondelivery"
                        onChange={(event) =>
                          setRows((current) => current.map((item) => (item.id === row.id ? { ...item, method: event.target.value } : item)))
                        }
                        disabled={!canManage}
                        aria-invalid={Boolean(problem)}
                        aria-describedby={error}
                      />
                    </Field>
                    <Field label="Account">
                      <Input
                        id={`erp-doc-row-${row.id}-account`}
                        autoComplete="off"
                        spellCheck={false}
                        value={row.account}
                        placeholder="5311"
                        onChange={(event) =>
                          setRows((current) => current.map((item) => (item.id === row.id ? { ...item, account: event.target.value } : item)))
                        }
                        disabled={!canManage}
                        aria-invalid={Boolean(problem)}
                        aria-describedby={error}
                      />
                    </Field>
                    {canManage ? (
                      <button
                        type="button"
                        className="button button--ghost button--small erp-doc-accounts__remove"
                        onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}
                        aria-label={`Remove ${row.method.trim() || 'this'} payment method`}
                      >
                        Remove
                      </button>
                    ) : null}
                    {problem ? (
                      <p id={error} className="field__help panel__note--danger erp-doc-accounts__error">
                        {problem}
                      </p>
                    ) : null}
                  </div>
                );
              })}
              {canManage ? (
                <div>
                  <button
                    type="button"
                    className="button button--secondary button--small"
                    onClick={() => {
                      setRows((current) => [...current, { id: nextRow, method: '', account: '' }]);
                      setNextRow((value) => value + 1);
                    }}
                  >
                    Add a payment method
                  </button>
                </div>
              ) : null}
            </fieldset>
          </>
        ) : null}
      </div>
      <ErrorNotice error={action.error} />
      {action.notice ? (
        <p className="panel__note" role="status">
          {action.notice}
        </p>
      ) : null}
      {canManage ? (
        <div className="erp-actions">
          <button
            type="button"
            className="button button--primary"
            disabled={action.busy !== null || !valid || !dirty}
            onClick={() => (turningOn ? setActivating(true) : void save())}
            data-testid="erp-save-documents"
          >
            {action.busy === 'save' ? 'Saving' : turningOn ? 'Review and turn on' : 'Save invoice settings'}
          </button>
        </div>
      ) : null}
      <ConfirmDialog
        open={activating}
        title={`Send invoices to ${erpName} automatically?`}
        description={`From now on every invoice from the day below is sent to ${erpName} without asking. A sent invoice is corrected in ${erpName}, not here.`}
        confirmLabel="Turn on sending"
        busy={action.busy === 'save'}
        onCancel={() => setActivating(false)}
        onConfirm={() => {
          setActivating(false);
          void save();
        }}
      >
        <dl className="erp-doc-detail__facts" data-testid="erp-activation-facts">
          <div>
            <dt>Company</dt>
            <dd>
              {companyName.trim() || connection.name} · {connection.company_identifier}
              {testCompany ? ' · test company' : ''}
            </dd>
          </div>
          <div>
            <dt>Invoices from</dt>
            <dd>{since ? formatDay(`${since}T00:00:00Z`) : 'Today'}</dd>
          </div>
          <div>
            <dt>Import</dt>
            <dd>{importMode === 'automatic' ? `${erpName} finishes it` : `${erpName} keeps it for a person to finish`}</dd>
          </div>
          <div>
            <dt>Receipts</dt>
            <dd>{receiptsAutomatic ? 'Sent on their own' : 'Sent by a person'}</dd>
          </div>
          {counts ? (
            <div>
              <dt>Built now</dt>
              <dd>
                {counts.ready} to send
                {counts.blocked > 0 ? ` · ${counts.blocked} need a fix and wait` : ''}
              </dd>
            </div>
          ) : null}
          {missing.length > 0 ? (
            <div>
              <dt>Not done</dt>
              <dd>{missing.map((id) => readinessLabel[id]).join(' · ')}</dd>
            </div>
          ) : null}
        </dl>
      </ConfirmDialog>
    </div>
  );
}
